import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Notice } from '../../../guest/notice/Notice'
import { Button } from '../../../buttons/Button'
import Icon from '../../../ui/Icon'

import { useResendConfirmation } from '../../../../hooks/auth/useResendConfirmation'
import { useApp } from '../../../../contexts/AppContext'
import { useAuth } from '../../../../contexts/AuthContext'
import { useProfileContext } from '../../../../contexts/ProfileContext'
import { BannerKind } from '../../../../types/contexts/AppContext'
import { getFromToken, JwtKeys } from '../../../../utils/jwtUtils'
import {
  CONFIRMATION_RESEND_COOLDOWN_MS,
  confirmationEmailSentAt,
  rememberConfirmationEmailSent,
} from '../../../../utils/profile/confirmationEmail'

const secondsLeft = (sentAt: number | null, now: number) =>
  sentAt === null ? 0 : Math.max(0, Math.ceil((sentAt + CONFIRMATION_RESEND_COOLDOWN_MS - now) / 1000))

/** Shown above the booking page until the user opens the link from the registration confirmation email. */
const EmailConfirmationNotice = () => {
  const { t } = useTranslation('bookings')
  const { showBanner } = useApp()
  const { authInfo } = useAuth()
  const { getProfile } = useProfileContext()
  const { resend, loading } = useResendConfirmation()
  const email = getFromToken(authInfo?.accessToken ?? '', JwtKeys.EMAIL) ?? ''
  const [sentAt, setSentAt] = useState(() => confirmationEmailSentAt(email))
  const [now, setNow] = useState(() => Date.now())
  const cooldown = secondsLeft(sentAt, now)
  const coolingDown = cooldown > 0

  useEffect(() => {
    if (!coolingDown) {
      return
    }
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [coolingDown])

  useEffect(() => {
    // The link is usually opened in another tab: pick up the confirmation when the user comes back to this one.
    const refresh = () => void getProfile()
    window.addEventListener('focus', refresh)
    return () => window.removeEventListener('focus', refresh)
  }, [getProfile])

  const handleResend = async () => {
    try {
      await resend()
      showBanner({ kind: BannerKind.OK, msg: t('booking.email-confirmation.resent', { email }) })
    } catch (caught) {
      showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message })
    }
    // Also after a rejected resend: the server refuses one within the cooldown, so there is nothing to retry yet.
    rememberConfirmationEmailSent(email)
    setSentAt(Date.now())
    setNow(Date.now())
  }

  return (
    <Notice
      className="fly-notice-warning mb-4"
      icon={<Icon name="mail" size={18} />}
      title={t('booking.email-confirmation.title')}
    >
      <p className="mb-2">{t('booking.email-confirmation.body', { email })}</p>
      <p className="mb-3">{t('booking.email-confirmation.hint')}</p>
      <Button
        size="sm"
        label={
          coolingDown
            ? t('booking.email-confirmation.resend-wait', { seconds: cooldown })
            : t('booking.email-confirmation.resend')
        }
        loading={loading}
        disabled={coolingDown}
        onClick={handleResend}
      />
    </Notice>
  )
}

export default EmailConfirmationNotice
